import React, { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useFocusEffect } from '@react-navigation/native';
import { RefreshControl, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { GET_CHECKLIST_SUBMISSIONS_AWAITING_MY_SIGN_OFF } from '../../../common/graphql/checklist';
import { formatChecklistSignOffTime } from '../../../common/utils/checklist/checklistSignOff';
import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { Text } from '../../components';
import { Card, EmptyState, Screen } from '../../design/components';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const ChecklistApprovalsView = ({ navigation }) => {
	const styles = useThemedStyles(createStyles);
	const { tenant } = useUserContext();
	const { data, loading, error, refetch } = useQuery(GET_CHECKLIST_SUBMISSIONS_AWAITING_MY_SIGN_OFF, {
		fetchPolicy: 'cache-and-network',
	});
	const refresh = useCallback(() => refetch().catch(() => {}), [refetch]);
	const [refreshPage, isRefreshing] = useRefreshPage(refresh);
	const submissions = data?.getChecklistSubmissionsAwaitingMySignOff || [];

	useFocusEffect(useCallback(() => {
		refresh();
	}, [refresh]));

	return (
		<Screen
			heading="Approvals"
			subheading="Vehicle checklists waiting for your sign-off"
			isLoading={loading && !submissions.length && !error}
			error={error ? 'Unable to load approvals. Check your connection and try again.' : undefined}
			onRetry={refreshPage}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			{!error && !loading && !submissions.length && (
				<EmptyState icon="check-square" title="All caught up" message="No checklists waiting for your sign-off" />
			)}
			{
				submissions.map(submission => {
					const vehicle = submission.vehicleDetails;
					const vehicleName = [vehicle?.name, vehicle?.registration].filter(Boolean).join(' · ') || 'Vehicle';
					const submittedTime = submission.submittedAt
						? getLocalTime(tenant?.timezone, submission.submittedAt)
						: null;
					const submittedDay = submittedTime?.isSame(getLocalTime(tenant?.timezone), 'day')
						? 'Today'
						: submittedTime?.format('ddd D MMM,');
					const submittedLabel = submittedTime
						? `${submittedDay} ${formatChecklistSignOffTime(tenant?.timezone, submission.submittedAt)}`
						: '';
					return (
						<Card
							key={submission.id}
							style={styles.row}
							accessibilityLabel={[vehicleName, getUserName(submission.user), submission.template?.title || 'Checklist', submittedLabel].filter(Boolean).join(', ')}
							accessibilityHint="Open checklist for sign-off"
							onPress={() => navigation.navigate('ChecklistSignOffView', { checklistSubmissionId: submission.id })}
						>
							<View style={styles.rowContent}>
								<Text style={styles.vehicle}>{vehicleName}</Text>
								<Text style={styles.details}>{getUserName(submission.user)}</Text>
								<Text style={styles.details}>{submission.template?.title || 'Checklist'}</Text>
								{Boolean(submittedLabel) && <Text style={styles.time}>{submittedLabel}</Text>}
							</View>
							<Feather name="chevron-right" size={22} color={styles.details.color} />
						</Card>
					);
				})
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	rowContent: {
		flex: 1,
	},
	vehicle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	details: {
		...type.bodySmall,
		color: colors.textSecondary,
		marginTop: space.xs,
	},
	time: {
		...type.secondary,
		color: colors.textSecondary,
		marginTop: space.sm,
	},
});

export default ChecklistApprovalsView;
